import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Clock3, Copy, Eye, FileText, GripVertical, ImageOff, LayoutGrid, MapPin, MoreVertical, Pencil, Phone, Plus, Search, Star, Tag, ToggleRight, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { cleanValues, EntityForm, type FieldSpec, useAssetUrls } from '../components/EntityForm';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';

export interface Facility {
  id: string;
  name: string;
  subtitle: string | null;
  description: string | null;
  icon: string;
  imageAssetId: string | null;
  openingHours: string | null;
  location: string | null;
  contact: string | null;
  category: string | null;
  featured: boolean;
  enabled: boolean;
}

/** Category → chip colours. Free categories (older data) fall back to grey. */
export const CATEGORIES: Record<string, [string, string]> = {
  Recreation: ['#1d4ed8', '#e8f0ff'],
  Wellness: ['#be185d', '#fde8f1'],
  Fitness: ['#6d28d9', '#f1ebff'],
  Family: ['#be123c', '#ffe9ee'],
  Business: ['#b45309', '#fff4e0'],
  Transport: ['#0f766e', '#e0f5f2'],
  Dining: ['#c2410c', '#fff1e6'],
  Services: ['#334155', '#eef1f5'],
};
export const catColor = (c: string | null) => CATEGORIES[c ?? ''] ?? ['#475569', '#eef1f5'];

export const facilityFields: FieldSpec[] = [
  { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Swimming Pool' },
  { key: 'subtitle', label: 'Short line on the card', type: 'text', placeholder: 'Relax and enjoy our outdoor pool' },
  { key: 'category', label: 'Category', type: 'select', options: [{ value: '', label: 'No category' }, ...Object.keys(CATEGORIES).map((c) => ({ value: c, label: c }))] },
  { key: 'icon', label: 'Icon', type: 'icon' },
  { key: 'imageAssetId', label: 'Photo', type: 'image', urlKey: 'imageUrl', hint: '16:9 photo, at least 1280×720.' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'openingHours', label: 'Opening hours', type: 'text', placeholder: 'Daily 7:00 AM – 8:00 PM', hint: 'Also “24 hours” or “On request”. Used for the Open now status.' },
  { key: 'location', label: 'Location', type: 'text', placeholder: 'Ground floor, beach side' },
  { key: 'contact', label: 'Contact', type: 'text', placeholder: 'Ext. 2100' },
  { key: 'featured', label: 'Featured (listed first on the TV)', type: 'toggle' },
  { key: 'enabled', label: 'Shown on TV', type: 'toggle' },
];

export type Status = 'open' | 'closed' | 'request' | 'unknown';
const TIME = String.raw`(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?`;
const RANGE = new RegExp(`${TIME}\\s*(?:-|–|—|to)\\s*${TIME}`, 'i');
const toMin = (h: string, m: string | undefined, ap: string | undefined) => {
  let hh = Number(h) % 24;
  const pm = ap?.toLowerCase().startsWith('p');
  if (ap) hh = (hh % 12) + (pm ? 12 : 0);
  return hh * 60 + Number(m ?? 0);
};
/** "Daily 7:00 AM – 8:00 PM" → [“Daily”, “7:00 AM – 8:00 PM”] and whether it is open at the hotel's current time. */
export function hoursOf(text: string | null, tz: string): { lines: [string, string?]; status: Status } {
  const t = (text ?? '').trim();
  if (!t) return { lines: ['Not set'], status: 'unknown' };
  if (/request|appointment|reception/i.test(t)) return { lines: ['On request', t.toLowerCase() === 'on request' ? undefined : t.replace(/^on request[,.\s–-]*/i, '') || undefined], status: 'request' };
  if (/24\s*(h|hours|\/\s*7)/i.test(t)) return { lines: ['24 hours', 'Open all day'], status: 'open' };
  const m = RANGE.exec(t);
  if (!m) return { lines: [t], status: 'unknown' };
  const from = toMin(m[1]!, m[2], m[3] ?? m[6]);
  const to = toMin(m[4]!, m[5], m[6]);
  const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz }).formatToParts(new Date());
  const now = Number(parts.find((p) => p.type === 'hour')?.value) * 60 + Number(parts.find((p) => p.type === 'minute')?.value);
  const open = from <= to ? now >= from && now < to : now >= from || now < to;
  const prefix = t.slice(0, m.index).replace(/[,:\s–-]+$/, '').trim();
  return { lines: prefix ? [prefix, m[0]] : [m[0]], status: open ? 'open' : 'closed' };
}
export const STATUS: Record<Status, { label: string; cls: string }> = {
  open: { label: 'Open now', cls: 'online' },
  closed: { label: 'Closed now', cls: 'disabled' },
  request: { label: 'By request', cls: 'info' },
  unknown: { label: 'No hours', cls: 'offline' },
};

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

export function FacilitiesPage() {
  const site = useSite();
  return <NeedsSite>{site ? <Facilities key={site.id} site={site} /> : <PageHeader title="Hotel Facilities" />}</NeedsSite>;
}

function Facilities({ site }: { site: Site }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const url = useAssetUrls();
  const key = ['facilities', site.id];
  const query = useQuery({ queryKey: key, queryFn: () => api.get<{ items: Facility[] }>(`/sites/${site.id}/facilities`).then((r) => r.items) });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const [order, setOrder] = useState<Facility[] | null>(null);
  const items = order ?? query.data ?? [];
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState('all');
  const [drag, setDrag] = useState<number | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const navigate = useNavigate();
  const setEdit = (f: Facility | 'new') => navigate(`/experience/facilities/${f === 'new' ? 'new' : f.id}`);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const editable = can('ADMIN');

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put(`/sites/${site.id}/facilities/order`, { ids }),
    onSettled: async () => {
      await invalidate();
      setOrder(null);
    },
  });
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<Facility> }) => api.patch(`/facilities/${id}`, body), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/facilities/${id}`), onSuccess: invalidate });
  const toForm = (f: Facility) => ({ ...f, category: f.category ?? '', imageUrl: url(f.imageAssetId) });
  const duplicate = useMutation({
    mutationFn: (f: Facility) => api.post(`/sites/${site.id}/facilities`, { ...cleanValues(toForm(f), facilityFields), name: `${f.name} (copy)`.slice(0, 80) }),
    onSuccess: invalidate,
  });
  const moveTo = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m!);
    reorder.mutate(next.map((x) => x.id));
    setOrder(next);
  };

  const hours = useMemo(() => new Map(items.map((f) => [f.id, hoursOf(f.openingHours, site.timezone)])), [items, site.timezone, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtering = q.trim() !== '' || cat !== 'all' || status !== 'all';
  const shown = items.filter((f) => {
    const s = q.trim().toLowerCase();
    if (s && ![f.name, f.subtitle, f.category].some((v) => v?.toLowerCase().includes(s))) return false;
    if (cat !== 'all' && (f.category ?? '') !== cat) return false;
    if (status === 'hidden') return !f.enabled;
    if (status !== 'all' && hours.get(f.id)?.status !== status) return false;
    return true;
  });
  const sel = items.find((f) => f.id === selId) ?? shown[0] ?? null;
  const cats = [...new Set(items.map((f) => f.category).filter(Boolean) as string[])].sort();

  if (query.isLoading) return <Loading />;
  return (
    <>
      <PageHeader title="Hotel Facilities" sub="Pool, spa, gym, beach, kids club… Shown as photo cards with a detail page on the TV." />
      <div className="stat-strip">
        <StatCard icon={<LayoutGrid size={22} />} tone="violet" value={items.length} label="Total facilities" sub="Manage all hotel facilities" />
        <StatCard icon={<ToggleRight size={22} />} tone="green" value={items.filter((f) => f.enabled).length} label="Enabled facilities" sub="Visible on TV" />
        <StatCard icon={<Star size={22} />} tone="amber" value={items.filter((f) => f.featured).length} label="Featured facilities" sub="Listed first on TV" />
        <StatCard icon={<Clock3 size={22} />} tone="violet" value={items.filter((f) => hours.get(f.id)?.status === 'open').length} label="Open now" sub={`At the hotel's time`} />
      </div>

      <div className="fac-layout">
        <div className="card fill">
          <div className="pkg-head">
            <span style={{ flex: 1 }}>
              <span className="row" style={{ gap: 8 }}>
                <span className="pkg-head-title">Facilities</span>
                <span className="pkg-count">{items.length}</span>
              </span>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Create and manage hotel facilities. Drag to reorder how they appear on the TV.
              </span>
            </span>
            {editable && (
              <button className="btn primary" onClick={() => setEdit('new')}>
                <Plus size={16} /> Add facility
              </button>
            )}
          </div>
          <div className="filters" style={{ marginTop: 14 }}>
            <label className="search">
              <Search size={16} />
              <input placeholder="Search facilities…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
              <option value="all">All categories</option>
              {cats.map((c) => (
                <option key={c}>{c}</option>
              ))}
              <option value="">No category</option>
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="all">All statuses</option>
              <option value="open">Open now</option>
              <option value="closed">Closed now</option>
              <option value="request">By request</option>
              <option value="hidden">Hidden on TV</option>
            </Select>
          </div>
          {shown.length === 0 ? (
            <Empty>{items.length ? 'No facility matches.' : 'No facilities yet. Add the first one; it appears on the TVs after saving.'}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="fac-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }} />
                    <th>Facility</th>
                    <th>Opening hours</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Enabled</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((f) => {
                    const i = items.indexOf(f);
                    const h = hours.get(f.id)!;
                    const [fg, bg] = catColor(f.category);
                    const img = url(f.imageAssetId);
                    return (
                      <tr
                        key={f.id}
                        className={`${sel?.id === f.id ? 'selected' : ''}${drag === i ? ' dragging' : ''}`}
                        style={!f.enabled ? { opacity: 0.55 } : undefined}
                        onClick={() => setSelId(f.id)}
                        draggable={editable && !filtering}
                        onDragStart={(e) => {
                          setDrag(i);
                          setOrder(items);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragOver={(e) => {
                          if (drag === null || drag === i) return;
                          e.preventDefault();
                          const next = [...items];
                          const [m] = next.splice(drag, 1);
                          next.splice(i, 0, m!);
                          setOrder(next);
                          setDrag(i);
                        }}
                        onDragEnd={() => {
                          setDrag(null);
                          if (order) reorder.mutate(order.map((x) => x.id));
                        }}
                      >
                        <td>
                          <span className="drag-handle" title={filtering ? 'Clear the filters to reorder' : 'Drag to reorder'}>
                            <GripVertical size={16} />
                          </span>
                        </td>
                        <td>
                          <span className="row" style={{ gap: 12, flexWrap: 'nowrap' }}>
                            <span className="fac-thumb">{img ? <img src={img} alt="" /> : <ImageOff size={18} />}</span>
                            <span style={{ minWidth: 0 }}>
                              <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                                <strong>{f.name}</strong>
                                {f.featured && <Star size={14} fill="#f59e0b" color="#f59e0b" aria-label="Featured" />}
                              </span>
                              {f.subtitle && <span className="muted fac-sub">{f.subtitle}</span>}
                            </span>
                          </span>
                        </td>
                        <td>
                          <span className="row" style={{ gap: 8, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                            <Clock3 size={16} style={{ marginTop: 2, flex: 'none' }} />
                            <span style={{ fontSize: 13 }}>
                              {h.lines[0]}
                              {h.lines[1] && <span className="muted" style={{ display: 'block' }}>{h.lines[1]}</span>}
                            </span>
                          </span>
                        </td>
                        <td>{f.category ? <span className="chip" style={{ color: fg, background: bg }}>{f.category}</span> : <span className="muted">—</span>}</td>
                        <td>
                          <span className={`state-pill ${STATUS[h.status].cls}`} style={{ whiteSpace: 'nowrap' }}>
                            <span className="state-dot" /> {STATUS[h.status].label}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Toggle on={f.enabled} onChange={(v) => editable && patch.mutate({ id: f.id, body: { enabled: v } })} label="Shown on TV" />
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          {editable && (
                            <>
                              <button className="icon-btn" title="Edit" onClick={() => setEdit(f)}>
                                <Pencil size={16} />
                              </button>
                              <button className="icon-btn" title="Duplicate" onClick={() => duplicate.mutate(f)}>
                                <Copy size={16} />
                              </button>
                              <button className="icon-btn" title="Delete" onClick={() => ask(`Delete ${f.name}? It disappears from the TVs.`, () => remove.mutate(f.id))}>
                                <Trash2 size={16} />
                              </button>
                              <Menu
                                minWidth={190}
                                button={({ onClick }) => (
                                  <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                    <MoreVertical size={16} />
                                  </button>
                                )}
                              >
                                <button type="button" className="more-item" onClick={() => patch.mutate({ id: f.id, body: { featured: !f.featured } })}>
                                  <Star size={15} /> {f.featured ? 'Remove from featured' : 'Feature it'}
                                </button>
                                <button type="button" className="more-item" disabled={i === 0} onClick={() => moveTo(i, 0)}>
                                  <ChevronUp size={15} /> Move to top
                                </button>
                                <button type="button" className="more-item" disabled={i === items.length - 1} onClick={() => moveTo(i, items.length - 1)}>
                                  <ChevronDown size={15} /> Move to bottom
                                </button>
                              </Menu>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <ErrorBox error={patch.error ?? remove.error ?? reorder.error ?? duplicate.error} />
        </div>

        {sel && <FacilityDetail f={sel} img={url(sel.imageAssetId)} hours={hours.get(sel.id)!} position={items.indexOf(sel) + 1} total={items.length} onEdit={editable ? () => setEdit(sel) : undefined} />}
      </div>

    </>
  );
}

function FacilityDetail({ f, img, hours, position, total, onEdit }: { f: Facility; img: string | null; hours: ReturnType<typeof hoursOf>; position: number; total: number; onEdit?: () => void }) {
  const [fg, bg] = catColor(f.category);
  const row = (icon: ReactNode, label: string, value: ReactNode) => (
    <div className="fac-detail-row">
      {icon}
      <span>{label}</span>
      <span className="fac-detail-val">{value}</span>
    </div>
  );
  return (
    <div className="card fac-detail">
      <div className="fac-hero">
        {img ? <img src={img} alt="" /> : <ImageOff size={30} />}
        {f.featured && (
          <span className="fac-featured">
            <Star size={13} fill="#f59e0b" color="#f59e0b" /> Featured
          </span>
        )}
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 16, flexWrap: 'nowrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 21 }}>{f.name}</h2>
        <span className={`state-pill ${STATUS[hours.status].cls}`} style={{ whiteSpace: 'nowrap' }}>
          <span className="state-dot" /> {STATUS[hours.status].label}
        </span>
      </div>
      {(f.description || f.subtitle) && <p className="muted fac-desc">{f.description || f.subtitle}</p>}
      <div className="fac-detail-list">
        {row(<Clock3 size={17} />, 'Opening hours', f.openingHours || '—')}
        {row(<Tag size={17} />, 'Category', f.category ? <span className="chip" style={{ color: fg, background: bg }}>{f.category}</span> : '—')}
        {f.location && row(<MapPin size={17} />, 'Location', f.location)}
        {f.contact && row(<Phone size={17} />, 'Contact', f.contact)}
        {row(<Eye size={17} />, 'Visibility', f.enabled ? 'Shown on TV' : 'Hidden')}
        {row(<Star size={17} />, 'Featured', f.featured ? <span className="badge ok">Yes</span> : 'No')}
        {row(<FileText size={17} />, 'Position', `${position} of ${total}`)}
      </div>
      {onEdit && (
        <button className="btn primary" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }} onClick={onEdit}>
          <Pencil size={16} /> Edit facility
        </button>
      )}
    </div>
  );
}
